import { legacy_connect as connect } from 'react-redux';
import AsioEnablementDialog from 'src/components/AsioEnablement/AsioEnablementDialog/AsioEnablementDialog';
import {
  hasPsaPoweredByAsioEntitlement,
  hasValidPsaInstance,
  provisionAsio,
  provisionPsaPackage,
  submitFeedback,
  checkSubDomainStatus,
  provisionAutomatePackage,
  validateSignupData
} from 'src/services/AsioService';
import { FEEDBACK_DIALOG_ID } from '../Feedback/FeedbackDialogContainer';
import * as core from 'cw-ui-core';
import Locale from 'src/Locale';

export const ASIO_ENABLEMENT_DIALOG_ID = 'ASIO_ENABLEMENT_DIALOG';

const mapStateToProps = state => {
  const productsCache = core.getModuleCache(state.module, ['partnerProducts']);
  const products = productsCache ? productsCache.toJS() : [];
  const manageProducts = products.filter(p =>
      p.name?.toLowerCase().includes('manage')
    );
  const automateProducts = products.filter(p => p.name?.toLowerCase().includes('automate'));
  const brightGaugeProducts = products.filter(p => p.name?.toLowerCase().includes('brightgauge'));

  return {
    isSuccess: core.getScreenData(state.screen, ['isSuccess']),
    isFullAccountUpgrade: core.getModuleCache(state.module, ['isFullAccountUpgrade']),
    isAutomateInAsioUpgrade: core.getModuleCache(state.module, ['isAutomateInAsioUpgrade']),
    isDefaultProvisioning: core.getModuleCache(state.module, ['isDefaultProvisioning']),
    selectedFeature: core.getModuleCache(state.module, ['selectedFeature']),
    asioDialogStep: core.getModuleCache(state.module, ['asioDialogActiveStep']),
    isBrightGaugeStepper: core.getScreenData(state.screen, ['isBrightGaugeStepper'], ASIO_ENABLEMENT_DIALOG_ID),
    isAsioFailureDialogOpen: core.getModuleCache(state.module, ['isAsioFailureDialogOpen']),
    asioDecommissioningResult: core.getModuleCache(state.module, ['asioDecommissioningResult'])?.toJS(),
    manageProducts,
    automateProducts,
    brightGaugeProducts
  };
};

const mapDispatchToProps = dispatch => {
  const state = core.Store().getState();
  const selectedFeature = core.getModuleCache(state.module, ['selectedFeature']);
  let isPsa = false;
  let isAutomate = false;

  if (selectedFeature === 'automate') {
    isAutomate = true;
  } else if (selectedFeature === 'psa') {
    isPsa = true;
  }

  const handleProvisioningResult = async (isPackageProvisioning = false, isAutomateProvisioning = false, instanceId) => {
    const result = isPackageProvisioning
      ? await provisionPsaPackage(instanceId) : isAutomateProvisioning
        ? await provisionAutomatePackage()
      : await provisionAsio();

    const isSuccess =
      result.isSuccess &&
      (!!result.response?.instanceId ||
        (!!result.response?.packageId &&
          ['success', 'partial'].includes(result.response?.status)));

    dispatch(core.setScreenData(['isSuccess'], !!isSuccess));
    dispatch(core.setModuleCache('provisioningResult', result));
    return result;
  };

  const handlePsaProvisioningFlow = async brightGaugeProducts => {
    const hasValidPsa = await hasValidPsaInstance();

    // Null or undefined means partner doesn't have a valid Classic PSA instance.
    if (hasValidPsa?.instanceId === null || hasValidPsa?.instanceId === undefined) {
      const subject = core.formatMessage(Locale.case_subject);
      const description = core.formatMessage(Locale.case_description, {
        Errors: (hasValidPsa?.missingFields || []).map(field => field.description).join(',')
      });
      return await submitFeedback(subject, description);
    }

    const result = await hasPsaPoweredByAsioEntitlement();

    if (result?.response) {
      if (brightGaugeProducts.length === 0) {
        dispatch(core.setScreenData('isBrightGaugeStepper', true, ASIO_ENABLEMENT_DIALOG_ID));
        return true;
      }
      
      return await handleProvisioningResult(true, false, hasValidPsa?.instanceId);
    }

    return await handleProvisioningResult();
  };

  const onLinkPSAInstanceClick = async () => {
    const state = core.Store().getState();
    const productsCache = core.getModuleCache(state.module, ['partnerProducts']);
    const isAutomateInAsioUpgrade = core.getModuleCache(state.module, ['isAutomateInAsioUpgrade']);
    const isFullAccountUpgrade = core.getModuleCache(state.module, ['isFullAccountUpgrade']);
    const products = productsCache?.toJS() || [];

    const manageProducts = products.filter(p =>
      p.name?.toLowerCase().includes('manage')
    );

    const brightGaugeProducts = products.filter(p =>
      p.name?.toLowerCase().includes('brightgauge')
    );

    if (isAutomate || isAutomateInAsioUpgrade) {
      return await handleProvisioningResult(false, true);
    }

    if (isPsa || isFullAccountUpgrade || manageProducts.length > 0) {
      return await handlePsaProvisioningFlow(brightGaugeProducts);
    }

    if (manageProducts.length === 0) {
      return await handleProvisioningResult();
    }
  };

  const provisionAsioAndBrightGauge = async () => {
    const hasValidPsa = await hasValidPsaInstance();
    const result = await handleProvisioningResult(true, false, hasValidPsa?.instanceId);
    return result;
  };

  const validateAsioProvisioningData = async () => {
    try {
      const result = await validateSignupData();
      if (result.isSuccess) {
        dispatch(core.setModuleCache('provisioningResult', []));
        return true;
      } else {
        dispatch(core.setModuleCache('provisioningResult', result?.errorMessages));
        return false;
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onViewCaseClick = linkPSAInstanceResult => {
    const state = core.Store().getState();
    const salesForceUrl = core.getModuleConfig(state.module, [
      'SalesForceCaseUrlBase'
    ]);
    const caseId = linkPSAInstanceResult.response?.id || linkPSAInstanceResult?.Id;
    const caseUrl = salesForceUrl + caseId;
    window.open(caseUrl, '_blank');
  };

  const validateBrightGaugeDetails = async () => {
    const state = core.Store().getState();
    const partnerCompany = core.getModuleCache(state.module, ['partner', 'companyName']) || '';
    const subDomain = partnerCompany.replace(/[^a-zA-Z0-9-]/g, '');
    const bgCompanyName = core.getScreenData(state.screen, ['companyName'], ASIO_ENABLEMENT_DIALOG_ID) || partnerCompany;
    const bgSubDomain = core.getScreenData(state.screen, ['subDomain'], ASIO_ENABLEMENT_DIALOG_ID) || subDomain;

    const errors = [];
    const subDomainRegex = /^[a-zA-Z0-9]+[a-zA-Z0-9-]*[a-zA-Z0-9]+$/;

    if (!subDomainRegex.test(bgSubDomain) || bgSubDomain.length > 45) {
      errors.push('Invalid subdomain or its length exceeds 45 characters');
    }

    if (bgCompanyName.length > 45) {
      errors.push('Company Name should not exceed 45 characters');
    }

    let result;
    if (!errors.length) {
      result = await checkSubDomainStatus(bgSubDomain);
      if (!result?.response?.data?.available) {
        errors.push(result?.response?.message || 'Failed to check subdomain status.');
      }
    }

    if (errors.length) {
      dispatch(core.setScreenData('brightGaugeValidationErrors', errors));
      return false;
    }

    return true;
  };

  return {
    onCloseIconClick: () => {
      //set the stepper to first step.
      dispatch(core.setModuleCache('asioDialogActiveStep', 0));
      dispatch(core.hideDialog(ASIO_ENABLEMENT_DIALOG_ID));
      dispatch(core.setModuleCache('provisioningResult', null));
      dispatch(core.removeModuleCache('isFullAccountUpgrade'));
      dispatch(core.removeModuleCache('isAutomateInAsioUpgrade'));
      dispatch(core.removeModuleCache('isAsioFailureDialogOpen'));
      dispatch(core.removeModuleCache('asioDecommissioningResult'));
    },
    onNeedSupportClick: (asioStep, isFullAccountUpgrade) => {
      dispatch(core.setModuleCache('asioDialogActiveStep', asioStep));
      dispatch(core.hideDialog(ASIO_ENABLEMENT_DIALOG_ID));
      dispatch(core.showDialog(FEEDBACK_DIALOG_ID, { isPreviousButtonVisible: isFullAccountUpgrade }));
    },
    clearBrightGaugeValidations: () => {
      dispatch(core.removeScreenData('brightGaugeValidationErrors'));
      dispatch(core.setScreenData('isBrightGaugeStepper', false, ASIO_ENABLEMENT_DIALOG_ID));
      dispatch(core.removeScreenData('subDomain', ASIO_ENABLEMENT_DIALOG_ID));
      dispatch(core.removeScreenData('companyName', ASIO_ENABLEMENT_DIALOG_ID));
    },
    onLinkPSAInstanceClick,
    validateAsioProvisioningData,
    onViewCaseClick,
    provisionAsioAndBrightGauge,
    validateBrightGaugeDetails
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AsioEnablementDialog);
